import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

export default function SellInquiryPage() {
  const [formData, setFormData] = useState({
    seller_name: '',
    artist_name: '',
    contact: '',
    email: '',
    artwork_name: '',
    artwork_type: '',
    artwork_size: '',
    desired_price: '',
    artwork_description: '',
    image_url: '',
    purchase_location: '',
    additional_notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemaData = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': '작품 판매 문의 - 온라인 미술 작품 전시 및 판매 플랫폼',
        'description': '미술 작품 판매를 원하시는 작가님들을 위한 문의 페이지입니다. 작품 정보와 판매 희망 가격을 입력해주세요.',
        'url': `${siteUrl}/sell-inquiry`,
        'inLanguage': 'ko-KR',
        'isPartOf': {
          '@type': 'WebSite',
          'url': siteUrl,
          'name': '온라인 미술 작품 전시 및 판매 플랫폼'
        },
        'breadcrumb': {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': '홈',
              'item': siteUrl
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': '작품 판매 문의',
              'item': `${siteUrl}/sell-inquiry`
            }
          ]
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': '작품 판매 문의',
        'description': '미술품 판매를 원하시는 작가 및 컬렉터를 위한 판매 문의 페이지입니다. 작가명, 작품명, 작품 스펙, 희망 판매가를 입력하여 미술품 판매를 신청하세요.',
        'url': `${siteUrl}/sell-inquiry`
      }
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = 'sell-inquiry-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('sell-inquiry-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.seller_name.trim()) {
      setErrorMessage('판매자 이름을 입력해주세요.');
      return false;
    }
    if (!formData.contact.trim()) {
      setErrorMessage('연락처를 입력해주세요.');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrorMessage('올바른 이메일 주소를 입력해주세요.');
      return false;
    }
    if (!formData.artwork_name.trim()) {
      setErrorMessage('작품명을 입력해주세요.');
      return false;
    }
    if (!formData.artwork_type.trim()) {
      setErrorMessage('작품 종류/재료를 선택해주세요.');
      return false;
    }
    if (!formData.artwork_size.trim()) {
      setErrorMessage('작품 크기를 입력해주세요.');
      return false;
    }
    if (!formData.desired_price.trim()) {
      setErrorMessage('희망 판매가를 입력해주세요.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const { error } = await supabase
        .from('applications')
        .insert([{
          name: formData.seller_name,
          email: formData.email,
          phone: formData.contact,
          event_type: 'sell_inquiry',
          event_date: new Date().toISOString().split('T')[0],
          media_type: formData.artwork_type,
          message: formData.artwork_description || '',
          additional_info: formData.additional_notes || '',
          media_url: formData.image_url || '',
          status: 'pending',
          basic_fields_data: {
            artist_name: formData.artist_name,
            artwork_name: formData.artwork_name,
            artwork_size: formData.artwork_size,
            desired_price: formData.desired_price,
            purchase_location: formData.purchase_location,
          },
          created_at: new Date().toISOString()
        }]);

      if (error) throw new Error(JSON.stringify(error));

      // 이메일 알림 발송
      try {
        await fetch('https://hmjykajnrjbypiksedtd.supabase.co/functions/v1/send-inquiry-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'sell_inquiry',
            data: {
              seller_name: formData.seller_name,
              contact: formData.contact,
              email: formData.email,
              artwork_name: formData.artwork_name,
              artist_name: formData.artist_name,
              artwork_type: formData.artwork_type,
              artwork_size: formData.artwork_size,
              desired_price: formData.desired_price,
              purchase_location: formData.purchase_location,
              artwork_description: formData.artwork_description,
              image_url: formData.image_url,
              additional_notes: formData.additional_notes,
            }
          })
        });
      } catch (emailErr) {
        console.error('Email notification failed:', emailErr);
      }

      setSubmitStatus('success');
      setFormData({
        seller_name: '',
        artist_name: '',
        contact: '',
        email: '',
        artwork_name: '',
        artwork_type: '',
        artwork_size: '',
        desired_price: '',
        artwork_description: '',
        image_url: '',
        purchase_location: '',
        additional_notes: ''
      });

      setTimeout(() => {
        window.REACT_APP_NAVIGATE('/');
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setErrorMessage('문의 등록 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" role="main">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm font-medium">돌아가기</span>
          </button>
          <h1 className="text-xl font-bold text-gray-900">작품 판매 문의</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Form Section */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-coral-500 to-golden-500 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-palette-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">작품 판매 문의</h2>
            <p className="text-gray-600 text-base leading-relaxed">
              판매를 원하시는 작품의 정보를 입력해주세요. 전문 큐레이터가 검토 후 연락드리겠습니다.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 판매자 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-user-line text-coral-500"></i>
                판매자 정보
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="seller_name" className="block text-sm font-medium text-gray-700 mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="seller_name"
                    name="seller_name"
                    value={formData.seller_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                    placeholder="판매자 이름을 입력해주세요"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="contact"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                    placeholder="010-0000-0000"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  이메일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                  placeholder="example@email.com"
                  required
                />
              </div>
            </div>

            {/* 작품 정보 */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-image-line text-coral-500"></i>
                작품 정보
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="artwork_name" className="block text-sm font-medium text-gray-700 mb-2">
                    작품명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="artwork_name"
                    name="artwork_name"
                    value={formData.artwork_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                    placeholder="작품명을 입력해주세요"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="artist_name" className="block text-sm font-medium text-gray-700 mb-2">
                    작가명
                  </label>
                  <input
                    type="text"
                    id="artist_name"
                    name="artist_name"
                    value={formData.artist_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                    placeholder="작가명을 입력해주세요"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="artwork_type" className="block text-sm font-medium text-gray-700 mb-2">
                    작품 종류/재료 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="artwork_type"
                    name="artwork_type"
                    value={formData.artwork_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    required
                  >
                    <option value="">선택해주세요</option>
                    <option value="회화 - 유화">회화 - 유화</option>
                    <option value="회화 - 아크릴">회화 - 아크릴</option>
                    <option value="회화 - 수채화">회화 - 수채화</option>
                    <option value="드로잉">드로잉</option>
                    <option value="판화">판화</option>
                    <option value="조각">조각</option>
                    <option value="사진">사진</option>
                    <option value="설치미술">설치미술</option>
                    <option value="미디어아트">미디어아트</option>
                    <option value="기타">기타</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="artwork_size" className="block text-sm font-medium text-gray-700 mb-2">
                    작품 크기 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="artwork_size"
                    name="artwork_size"
                    value={formData.artwork_size}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                    placeholder="예: 50 x 70 cm"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="desired_price" className="block text-sm font-medium text-gray-700 mb-2">
                  희망 판매가 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="desired_price"
                  name="desired_price"
                  value={formData.desired_price}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                  placeholder="예: 1,000,000원 또는 협의 가능"
                  required
                />
              </div>

              <div>
                <label htmlFor="artwork_description" className="block text-sm font-medium text-gray-700 mb-2">
                  작품 설명
                </label>
                <textarea
                  id="artwork_description"
                  name="artwork_description"
                  value={formData.artwork_description}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="작품에 대한 설명을 입력해주세요 (최대 500자)"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.artwork_description.length}/500</p>
              </div>

              <div>
                <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-2">
                  작품 이미지 URL
                </label>
                <input
                  type="url"
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">작품 이미지 링크를 입력해주세요</p>
              </div>

              <div>
                <label htmlFor="purchase_location" className="block text-sm font-medium text-gray-700 mb-2">
                  구매처
                </label>
                <input
                  type="text"
                  id="purchase_location"
                  name="purchase_location"
                  value={formData.purchase_location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm"
                  placeholder="예: 갤러리명, 온라인 쇼핑몰 URL 등"
                />
                <p className="text-xs text-gray-500 mt-1">작품을 구매할 수 있는 곳을 입력해주세요</p>
              </div>
            </div>

            {/* 추가 요청사항 */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-message-3-line text-coral-500"></i>
                추가 요청사항
              </h3>

              <div>
                <label htmlFor="additional_notes" className="block text-sm font-medium text-gray-700 mb-2">
                  기타 문의사항
                </label>
                <textarea
                  id="additional_notes"
                  name="additional_notes"
                  value={formData.additional_notes}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="추가로 전달하실 내용이 있으시면 입력해주세요 (최대 500자)"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.additional_notes.length}/500</p>
              </div>
            </div>

            {/* Status Messages */}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <p className="text-red-700 text-sm">{errorMessage}</p>
              </div>
            )}

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                <p className="text-green-700 text-sm">문의가 성공적으로 등록되었습니다. 곧 연락드리겠습니다.</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-gradient-to-r from-coral-500 to-golden-500 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-base cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  제출 중...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-line"></i>
                  문의 등록하기
                </span>
              )}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}